import UUID from 'uuid-js';
import { readOnly } from '@ember/object/computed';
import { isPresent } from '@ember/utils';
import { setProperties } from '@ember/object';
import { Cart as CartProto } from '@square/dashboard-protos/models/squareup/bills/cart';
import { underscore, camelize } from '@square/dashboard-utilities/utils/keys';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import TokenPair from '@square/dashboard-data/models/squareup/common/token/token-pair';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import DiningOptionLineItem from '@square/dashboard-items-data/models/invoices/cart/dining-option-line-item';
import FeatureDetails from '@square/dashboard-items-data/models/invoices/cart/feature-details';
import CartCalculator from '@square/dashboard-items-data/models/invoices/cart/cart-calculator';
import Itemization from '@square/dashboard-items-data/models/invoices/cart/cart-itemization';
import DiscountLineItem from '@square/dashboard-items-data/models/invoices/cart/discount-line-item';
import FeeLineItem from '@square/dashboard-items-data/models/invoices/cart/fee-line-item';
import SurchargeLineItem from '@square/dashboard-items-data/models/invoices/cart/surcharge-line-item';
import { isApportionedSurcharge } from '@square/dashboard-items-data/utils/invoices/cart-calculator';

/**
 * Types for this model are defined here: @dashboard/invoices-shared/types/model
 */

// squareup.bills.Cart.Amounts
const Amounts = ProtoBaseModel.extend({
  discountMoney: attr(MoneyProto),
  surchargeMoney: attr(MoneyProto),
  taxMoney: attr(MoneyProto),
  tipMoney: attr(MoneyProto),
  totalMoney: attr(MoneyProto),
  clone() {
    return Amounts.deserialize(Amounts.serialize(this));
  }
});

// squareup.bills.Cart.LineItems
const LineItems = ProtoBaseModel.extend({
  itemizations: repeated(Itemization, {
    key: 'itemization',
    defaultValue: []
  }),
  fees: repeated(FeeLineItem, {
    key: 'fee',
    defaultValue: []
  }),
  discounts: repeated(DiscountLineItem, {
    key: 'discount',
    defaultValue: []
  }),
  defaultDiningOption: attr(DiningOptionLineItem),
  surcharges: repeated(SurchargeLineItem, {
    key: 'surcharge',
    defaultValue: []
  }),
  clone() {
    const lineItemsClone = LineItems.create({
      itemizations: this.itemizations.map(itemization => itemization.clone()),
      fees: this.fees.map(fee => fee.clone()),
      discounts: this.discounts.map(discount => discount.clone()),
      defaultDiningOption: this.defaultDiningOption && this.defaultDiningOption.clone(),
      surcharges: this.surcharges.map(surcharge => surcharge.clone())
    });
    return lineItemsClone;
  }
});
function _getFeeLineItemVersion(feeLineItem) {
  return feeLineItem.get('writeOnlyBackingDetails.fee.catalogObjectReference.version');
}

// squareup.bills.Cart
const Cart = ProtoBaseModel.extend({
  amounts: attr(Amounts),
  lineItems: attr(LineItems),
  featureDetails: attr(FeatureDetails),
  roundingType: null,
  features: null,
  discounts: readOnly('lineItems.discounts'),
  itemizations: readOnly('lineItems.itemizations'),
  surcharges: readOnly('lineItems.surcharges'),
  addItemization(itemization) {
    if (!this.itemizations.includes(itemization)) {
      this.itemizations.pushObject(itemization);
      const itemizationDiscounts = itemization.get('configuration.selectedOptions.discounts') || [];
      const itemLevelDiscountsOnItemization = itemizationDiscounts.filterBy('isApplicationScopeItemLevel');
      itemLevelDiscountsOnItemization.forEach(discountLineItem => {
        this._addItemLevelDiscount(discountLineItem, itemization);
      });
    }
    // TODO(ryanly): ONCALL-72856 - Refactor edit itemization to not mutate the underlying itemization
    // The problem is that modified itemizations are passed to addItemization instead of updateItemization, so we
    // need to apply cart level discounts to these modified but new itemizations
    this._applyCartLevelDiscounts(itemization);
    this._applyApportionedServiceCharges(itemization);
    this._updateItemizationLevelFeeVersions();
    this.calculateTotals();
    this._removeInvalidCartBlocklistedTaxIds();
    this._recalculateAppointmentOrdinals();
  },
  deleteItemization(itemization) {
    const itemizationDiscounts = itemization.get('configuration.selectedOptions.discounts') || [];
    this._removeItemLevelDiscounts(itemizationDiscounts.filterBy('isApplicationScopeItemLevel'));
    this.itemizations.removeObject(itemization);
    this.calculateTotals();
    this._recalculateAppointmentOrdinals();
  },
  updateItemization(originalItemization, newItemization) {
    this._updateItemLevelDiscounts(originalItemization, newItemization);
    const index = this.itemizations.indexOf(originalItemization);
    this.itemizations.replace(index, 1, [newItemization]);
    this._applyCartLevelDiscounts(newItemization);
    this._applyApportionedServiceCharges(newItemization);
    this._updateItemizationLevelFeeVersions();
    this.calculateTotals();
    this._removeInvalidCartBlocklistedTaxIds();
    this._recalculateAppointmentOrdinals();
  },
  setItemizations(newItemizations) {
    this.set('lineItems.itemizations', newItemizations);
    this._updateItemizationLevelFeeVersions();
    this.calculateTotals();
    this._removeInvalidCartBlocklistedTaxIds();
    this._recalculateAppointmentOrdinals();
  },
  addDiscount(discount) {
    const discountId = discount.get('writeOnlyBackingDetails.discount.id');
    const existingDiscount = this.discounts.findBy('writeOnlyBackingDetails.discount.id', discountId);
    if (existingDiscount) {
      this.discounts.removeObject(existingDiscount);
    }
    this.discounts.pushObject(discount);
    (this.itemizations || []).mapBy('configuration').forEach(configuration => {
      if (!configuration.get('selectedOptions')) {
        configuration.set('selectedOptions', Itemization.Configuration.SelectedOptions.create());
      }
      configuration.set('selectedOptions.discounts', configuration.get('selectedOptions.discounts').rejectBy('writeOnlyBackingDetails.discount.id', discountId));
      configuration.get('selectedOptions.discounts').pushObject(discount.clone());
    });
    this.calculateTotals();
  },
  // TODO(ryanly/INVOICES-6860): Make sure discounts passed in are cart level discount objects (The current state is that this is
  // accepting item level discount objects)
  removeDiscount(discount) {
    const discountId = discount.get('writeOnlyBackingDetails.discount.id');
    // We need to reject discounts by discountId because this can include both cart level and item level discounts.
    // This means we cannot just remove the discount from the discounts array by reference, as the reference passed in
    // might not neccesarily be a cart level
    this.set('lineItems.discounts', this.discounts.rejectBy('writeOnlyBackingDetails.discount.id', discountId));
    (this.itemizations || []).mapBy('configuration').forEach(configuration => {
      if (!configuration.get('selectedOptions')) {
        configuration.set('selectedOptions', Itemization.Configuration.SelectedOptions.create());
      }
      configuration.set('selectedOptions.discounts', configuration.get('selectedOptions.discounts').rejectBy('writeOnlyBackingDetails.discount.id', discountId));
    });
    this.calculateTotals();
  },
  replaceDiscount(oldDiscountLineItem, newDiscountLineItem) {
    this.removeDiscount(oldDiscountLineItem);
    this.addDiscount(newDiscountLineItem);
    this.calculateTotals();
  },
  addSurcharge(surcharge) {
    this.surcharges.pushObject(surcharge);
    this.calculateTotals();
    this._removeInvalidCartBlocklistedTaxIds();
  },
  deleteSurcharge(surchargeId) {
    const surcharge = this.surcharges.findBy('backingDetails.surcharge.id', surchargeId);
    this.surcharges.removeObject(surcharge);
    this.calculateTotals();
  },
  updateSurcharge(originalSurchargeLineItem, newSurchargeLineItem) {
    const originalSurchargeId = originalSurchargeLineItem.backingDetails.surcharge.id;
    const originalSurcharge = this.surcharges.findBy('backingDetails.surcharge.id', originalSurchargeId);
    this.surcharges.removeObject(originalSurcharge);
    this.addSurcharge(newSurchargeLineItem);
  },
  // calculateTotals must be called after every cart modification so the cart totals are properly calculated
  // This will recompute all the fields on the cart
  calculateTotals() {
    new CartCalculator(this.roundingType).adjustTotals(this);
  },
  // TODO(ryanly/INVOICES-6860): Modify implementation to use addDiscount
  // Adds the item level discount to the top level discounts array
  // Also replaces the existing item level discount with the given one (If applicable)
  _addItemLevelDiscount(discountLineItem, itemization) {
    const discountId = discountLineItem.get('writeOnlyBackingDetails.discount.id');
    if (!this.discounts.findBy('writeOnlyBackingDetails.discount.id', discountId)) {
      this.discounts.pushObject(discountLineItem);
      const configuration = itemization.get('configuration');
      configuration.set('selectedOptions.discounts', configuration.get('selectedOptions.discounts').rejectBy('writeOnlyBackingDetails.discount.id', discountId));
      configuration.get('selectedOptions.discounts').pushObject(discountLineItem.clone());
      this.calculateTotals();
    }
  },
  // TODO(ryanly/INVOICES-6860): Modify implementation to pass a top level discount object to removeDiscount
  // Removes specified item level discounts from the top level discounts if able
  // Note: This does not remove them from the actual itemization
  _removeItemLevelDiscounts(discountsToRemove) {
    // Use compact as there exists cases where there are no discounts in an item's selectedOptions
    const allItemLevelDiscountsOfAllItemizations = this.itemizations.mapBy('configuration.selectedOptions.discounts').compact();

    // Get all item level discounts of all itemizations as one array
    const allItemLevelDiscounts = allItemLevelDiscountsOfAllItemizations.flatMap(instance => instance.toArray()).filterBy('isApplicationScopeItemLevel');

    // for each of the discounts to possibly remove from cart.discounts,
    // remove them only if only this current itemization has it as an item level discount
    discountsToRemove.forEach(discountLineItem => {
      const discountId = discountLineItem.get('writeOnlyBackingDetails.discount.id');
      const instancesOfDiscountLineItem = allItemLevelDiscounts.filterBy('writeOnlyBackingDetails.discount.id', discountId);

      // check if any other itemizations have this item level discount.
      // If yes, dont remove it from cart discounts else remove it
      if (instancesOfDiscountLineItem.length === 1) {
        this.removeDiscount(discountLineItem);
      }
    });
    this.calculateTotals();
  },
  // method that settles which item level discounts to add/remove
  // as an itemization is updated
  _updateItemLevelDiscounts(originalItemization, newItemization) {
    const originalItemizationItemLevelDiscounts = (originalItemization.get('configuration.selectedOptions.discounts') || []).filterBy('isApplicationScopeItemLevel');
    const originalItemizationItemLevelDiscountsIds = new Set(originalItemizationItemLevelDiscounts.mapBy('writeOnlyBackingDetails.discount.id'));
    const newItemizationItemLevelDiscounts = (newItemization.get('configuration.selectedOptions.discounts') || []).filterBy('isApplicationScopeItemLevel');
    const newItemizationItemLevelDiscountIds = new Set(newItemizationItemLevelDiscounts.mapBy('writeOnlyBackingDetails.discount.id'));
    const discountIdsToRemove = new Set([...originalItemizationItemLevelDiscountsIds].filter(discountId => !newItemizationItemLevelDiscountIds.has(discountId)));
    const discountsToRemove = originalItemizationItemLevelDiscounts.filter(discount => discountIdsToRemove.has(discount.writeOnlyBackingDetails.discount.id));
    const discountIdsToAdd = new Set([...newItemizationItemLevelDiscountIds].filter(discountId => !originalItemizationItemLevelDiscountsIds.has(discountId)));
    const discountsToAdd = newItemizationItemLevelDiscounts.filter(discount => discountIdsToAdd.has(discount.writeOnlyBackingDetails.discount.id));

    // In removing an item level discount, we need to remove it from cart.discounts too
    // An item level discount exists in cart.discounts list if one or more itemizations have this discount.
    //
    // Hence, before removing it from cart.discounts, We need to ensure that only this itemization
    // has this discount at item level.
    if (discountsToRemove.length > 0) {
      this._removeItemLevelDiscounts([...discountsToRemove]);
    }
    if (discountsToAdd.length > 0) {
      discountsToAdd.forEach(discountLineItem => {
        this._addItemLevelDiscount(discountLineItem, newItemization);
      });
    }
    this.calculateTotals();
  },
  _applyCartLevelDiscounts(itemization) {
    (this.discounts || []).filterBy('applicationScope', 'CART_LEVEL').forEach(discountLineItem => {
      const configuration = itemization.get('configuration');
      if (!configuration.get('selectedOptions')) {
        configuration.set('selectedOptions', Itemization.Configuration.SelectedOptions.create());
      }
      const discounts = configuration.get('selectedOptions.discounts');
      const incomingDiscountId = discountLineItem.get('writeOnlyBackingDetails.discount.id');
      if (!discounts.findBy('writeOnlyBackingDetails.discount.id', incomingDiscountId)) {
        discounts.pushObject(discountLineItem.clone());
      }
    });
    this.calculateTotals();
  },
  _applyApportionedServiceCharges(itemization) {
    (this.surcharges || []).filter(surcharge => isApportionedSurcharge(surcharge)).forEach(apportionedSurcharge => {
      const configuration = itemization.get('configuration');
      if (!configuration.get('selectedOptions')) {
        configuration.set('selectedOptions', Itemization.Configuration.SelectedOptions.create());
      }
      const surcharges = configuration.get('selectedOptions.surcharges');
      const incomingSurchargeId = apportionedSurcharge.get('backingDetails.surcharge.id');
      if (!surcharges.findBy('backingDetails.surcharge.id', incomingSurchargeId)) {
        surcharges.pushObject(apportionedSurcharge.clone());
      }
    });
    this.calculateTotals();
  },
  // We should only have one version of each fee on the cart, so we need to update every fee to the latest version
  _updateItemizationLevelFeeVersions() {
    const mostRecentFeeLineItems = {};
    const itemizations = this.itemizations === undefined ? [] : this.itemizations;
    itemizations.forEach(itemization => (itemization.get('configuration.selectedOptions.fees') === undefined ? [] : itemization.get('configuration.selectedOptions.fees')).forEach(feeLineItem => {
      const id = feeLineItem.get('writeOnlyBackingDetails.fee.id');
      const mostRecentFeeLineItem = mostRecentFeeLineItems[id];
      if (isPresent(mostRecentFeeLineItem)) {
        const currentVersion = _getFeeLineItemVersion(feeLineItem);
        const mostRecentFeeLineItemVersion = _getFeeLineItemVersion(mostRecentFeeLineItem);
        if (currentVersion > mostRecentFeeLineItemVersion) {
          mostRecentFeeLineItems[id] = feeLineItem;
        }
      } else {
        mostRecentFeeLineItems[id] = feeLineItem;
      }
    }));

    // Set each itemization to use the most recently used version of a fee
    itemizations.forEach(itemization => {
      const fees = itemization.get('configuration.selectedOptions.fees') === undefined ? [] : itemization.get('configuration.selectedOptions.fees');
      if (isPresent(fees)) {
        const mostRecentFees = fees.map(feeLineItem => {
          const id = feeLineItem.get('writeOnlyBackingDetails.fee.id');
          const mostRecentFeeLineItem = mostRecentFeeLineItems[id];
          const currentVersion = _getFeeLineItemVersion(feeLineItem);
          const mostRecentFeeLineItemVersion = _getFeeLineItemVersion(mostRecentFeeLineItem);
          if (currentVersion !== mostRecentFeeLineItemVersion) {
            const clonedFeeLineItem = FeeLineItem.deserialize(FeeLineItem.serialize(mostRecentFeeLineItem));
            // Generate a new token pair for this fee
            clonedFeeLineItem.set('feeLineItemTokenPair', TokenPair.create({
              clientToken: UUID.create().toString()
            }));
            return clonedFeeLineItem;
          } else {
            return feeLineItem;
          }
        });
        if (!itemization.get('configuration.selectedOptions')) {
          this.set('itemization.configuration.selectedOptions', Itemization.Configuration.SelectedOptions.create());
        }
        itemization.set('configuration.selectedOptions.fees', mostRecentFees);
      }
    });
  },
  // We don't support adding cart level blocklisted tax ids on Dashboard, but we need to remove any cart level
  // blocklisted tax ids if they were added to the cart on Dashboard. This is so they will no longer be removed when
  // the cart goes through the pricing engine on other platforms.
  _removeInvalidCartBlocklistedTaxIds() {
    const blocklistedTaxIds = this.featureDetails?.pricingEngineState?.blocklistedTaxIds;
    if (blocklistedTaxIds) {
      const currentFeeIds = this.lineItems.fees.mapBy('writeOnlyBackingDetails.fee.id');
      this.set('featureDetails.pricingEngineState.blocklistedTaxIds', blocklistedTaxIds.filter(blockedTaxId => !currentFeeIds.includes(blockedTaxId)));
    }
  },
  _recalculateAppointmentOrdinals() {
    this.itemizations.forEach((itemization, index) => {
      // Set cart ordinal for featureDetails.appointmentsServiceDetails if exists. https://jira.sqprod.co/browse/ONCALL-130251.
      if (itemization.featureDetails?.appointmentsServiceDetails) {
        itemization.set('featureDetails.appointmentsServiceDetails.ordinal', index);
      }
    });
  },
  toProto() {
    // fromObject handles the string to proto enums conversion
    return CartProto.fromObject(camelize(this.constructor.serialize(this)));
  },
  clone() {
    const cartClone = Cart.create({
      amounts: this.amounts && this.amounts.clone(),
      lineItems: this.lineItems && this.lineItems.clone(),
      featureDetails: this.featureDetails && this.featureDetails.clone(),
      roundingType: this.roundingType,
      features: this.features
    });
    cartClone.calculateTotals();
    return cartClone;
  }
}).reopenClass({
  Amounts,
  LineItems,
  createDefault(roundingType, features) {
    const cart = this.create({
      lineItems: LineItems.create(),
      featureDetails: FeatureDetails.createDefault(),
      roundingType,
      features
    });
    // Computed missing fields and totals
    cart.calculateTotals();
    return cart;
  },
  createDefaultForTemplate(roundingType, features) {
    const cart = this.create({
      lineItems: LineItems.create(),
      featureDetails: FeatureDetails.create(),
      // does not create invoiceTokenPair as template doesn't need it
      roundingType,
      features
    });
    // Computed missing fields and totals
    cart.calculateTotals();
    return cart;
  },
  fromProto(cartProto, roundingType, features, lateFeeInstance) {
    if (isPresent(cartProto)) {
      // We need to convert the proto to an object with the enums replaced as strings
      const cartJSObject = CartProto.toObject(cartProto, {
        enums: String,
        arrays: false
      });
      const cartModel = this.deserialize(underscore(cartJSObject));
      const additionalProperties = {
        features,
        roundingType
      };
      if (lateFeeInstance) {
        additionalProperties.lateFeeInstance = lateFeeInstance;
      }
      setProperties(cartModel, additionalProperties);
      return cartModel;
    }
    return undefined;
  },
  /**
   * Takes in a cart proto, converts it to a cart model, recalculates the cart, and converts it back to a cart proto.
   * This method was implemented to address an ONCALL issue (url below) that caused discounts to be calculated
   * differently on the client and the server. The order in which discounts are applied on the server depends on
   * COGS ids which we don't do on the client. This method is intended to be used by a cart proto using
   * COGS ids so that discounts are applied in the same way as the server.
   *
   * https://jira.sqprod.co/browse/INVOICES-12203
   *
   * @param {CartProto} cartProto  - cart with COGS ids
   * @returns {CartProto} recalculated cart
   */
  calculateCartProto(cartProto, roundingType, features) {
    const cartModel = Cart.fromProto(cartProto, roundingType, features);
    cartModel.calculateTotals();
    return cartModel.toProto();
  }
});
export default Cart;